import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Rocket, TestTube, AlertCircle, CheckCircle } from 'lucide-react'
import { triggerCampaignLaunchNotification, testCampaignLaunchNotification } from '@/lib/campaign-launch-trigger'
import { toast } from 'sonner'

export function CampaignLaunchNotificationTest() {
  const [campaignId, setCampaignId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null)

  const handleTestNotification = async () => {
    setIsLoading(true)
    setLastResult(null)

    try {
      const result = await testCampaignLaunchNotification()
      setLastResult(result)
      
      if (result.success) {
        toast.success('Test notification sent successfully!')
      } else {
        toast.error(`Test failed: ${result.message}`)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setLastResult({ success: false, message: errorMessage })
      toast.error(`Test error: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleTriggerNotification = async () => {
    if (!campaignId.trim()) {
      toast.error('Please enter a campaign ID')
      return
    }

    setIsLoading(true)
    setLastResult(null)

    try {
      const result = await triggerCampaignLaunchNotification(campaignId.trim())
      setLastResult(result)
      
      if (result.success) {
        toast.success('Campaign launch notification sent!')
      } else {
        toast.error(`Notification failed: ${result.message}`)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      setLastResult({ success: false, message: errorMessage })
      toast.error(`Error: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Rocket className="h-5 w-5" />
          Campaign Launch Notification Test
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="test-notification">Test with Mock Data</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Send a test notification using mock campaign data to verify the system works.
            </p>
            <Button 
              onClick={handleTestNotification}
              disabled={isLoading}
              variant="outline"
              className="w-full"
            >
              <TestTube className="h-4 w-4 mr-2" />
              {isLoading ? 'Sending Test...' : 'Send Test Notification'}
            </Button>
          </div>

          <div className="border-t pt-4">
            <Label htmlFor="campaign-id">Trigger for Real Campaign</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Enter a campaign ID to send a notification for an actual campaign.
            </p>
            <div className="flex gap-2">
              <Input
                id="campaign-id"
                placeholder="Enter campaign ID..."
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                disabled={isLoading}
              />
              <Button 
                onClick={handleTriggerNotification}
                disabled={isLoading || !campaignId.trim()}
              >
                <Rocket className="h-4 w-4 mr-2" />
                {isLoading ? 'Sending...' : 'Send Notification'}
              </Button>
            </div>
          </div>
        </div>

        {lastResult && (
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-2">
              {lastResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <span className="font-medium">Result:</span>
              <Badge variant={lastResult.success ? 'default' : 'destructive'}>
                {lastResult.success ? 'Success' : 'Failed'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {lastResult.message}
            </p>
          </div>
        )}

        <div className="border-t pt-4">
          <h4 className="font-medium mb-2">How it works:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Client Channel:</strong> Sends notification to the client's main Slack channel</li>
            <li>• <strong>Master Channel:</strong> Sends detailed notification to #campaigns-launched</li>
            <li>• <strong>Auto-trigger:</strong> Notifications are automatically sent when campaigns go live</li>
            <li>• <strong>Manual trigger:</strong> Use this tool to test or manually send notifications</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}
